import { ESTADOS_PADRON } from './estado-padron';
import type { EstadoPadron, PadronSummary } from './types';

/** `null` = sin filtro de estado (todos). */
export type FiltroEstado = EstadoPadron | null;

export interface FiltroPadrones {
  estado: FiltroEstado;
  busqueda: string;
}

/** Marcas diacríticas Unicode, para comparar "Padrón" con "padron". */
const DIACRITICOS = /[̀-ͯ]/g;

/**
 * Texto comparable: sin acentos, minúsculas y sin espacios de más. Los nombres
 * de archivo del padrón vienen con acentos y con el prefijo "Inconsistencias —"
 * (em-dash), así que buscar "padron" tiene que encontrar "Padrón" y buscar
 * "inconsistencias" tiene que encontrar las tres capas de re-subida.
 */
function norm(v: string | undefined): string {
  return (v ?? '')
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Aplica estado + búsqueda por nombre de archivo. Mantiene el orden de entrada. */
export function filtrarPadrones(
  padrones: PadronSummary[],
  filtro: FiltroPadrones,
): PadronSummary[] {
  const q = norm(filtro.busqueda);
  return padrones.filter((p) => {
    if (filtro.estado && p.estado !== filtro.estado) return false;
    if (q && !norm(p.nombreArchivo).includes(q)) return false;
    return true;
  });
}

/**
 * Cuántos padrones hay por estado, para mostrarlo en las opciones del filtro.
 *
 * Incluye los estados en CERO a propósito: el problema que motivó esta pantalla
 * fue justamente no encontrar padrones "pendiente de validación" y no poder
 * distinguir "no existen" de "no los estoy viendo". Un estado sin padrones tiene
 * que verse como 0, no desaparecer de la lista.
 *
 * Un estado desconocido (que el back agregue y el front todavía no) suma igual
 * al total, así que la cuenta de "Todos" nunca miente.
 */
export function contarPorEstado(padrones: PadronSummary[]): Record<string, number> {
  const counts: Record<string, number> = { todos: padrones.length };
  for (const e of ESTADOS_PADRON) counts[e] = 0;
  for (const p of padrones) {
    counts[p.estado] = (counts[p.estado] ?? 0) + 1;
  }
  return counts;
}
